import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import config from '../config';

let accessToken: string | null = null;
/**
 * Set by `auth/impersonation.ts` while an admin is impersonating an
 * operator. Takes precedence over `accessToken` in the request interceptor —
 * during impersonation, every API call carries the operator's identity
 * (with the admin recorded via the JWT's `act` claim, parsed by the backend).
 */
let impersonationToken: string | null = null;

export function setApiAccessToken(token: string | null): void {
  accessToken = token;
}

export function setImpersonationAccessToken(token: string | null): void {
  impersonationToken = token;
}

/**
 * M2 — single token-attachment path. The AuthContext registers the auth SDK
 * here on boot; the response interceptor uses it to refresh on 401 and retry
 * the original request. Without this, API calls failed hard when the access
 * token expired between the SDK's scheduled refresh (suspended tab, sleep)
 * and the next call — even though the SDK could have refreshed.
 *
 * The interface is just the subset of `Auth` we actually call so callers can
 * mock it in tests without dragging in the whole SDK type surface.
 */
interface RefreshableAuthSdk {
  refreshToken(): Promise<unknown>;
  getAccessToken(): string | null;
}

let authSdk: RefreshableAuthSdk | null = null;

export function setAuthSdk(sdk: RefreshableAuthSdk | null): void {
  authSdk = sdk;
}

export const axiosInstance = axios.create({
  baseURL: config.get('API_BASE_URL'),
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

axiosInstance.interceptors.request.use((req: InternalAxiosRequestConfig) => {
  // Impersonation token wins when active — the admin is acting AS an
  // operator and the backend audits both identities via the token's `act` claim.
  const token = impersonationToken ?? accessToken;
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  } else {
    delete req.headers.Authorization;
  }
  return req;
});

// Marker on the config so we only retry once per original request — otherwise
// a still-401 response after refresh would loop forever.
type RetriedConfig = InternalAxiosRequestConfig & { __retriedAfterRefresh?: boolean };

axiosInstance.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const original = err.config as RetriedConfig | undefined;
    // Impersonation tokens are not refreshable (no refresh token grant for
    // them by design — TTL is the safety). On 401 during impersonation,
    // clear the token slot, drop sessionStorage via a global event, and let
    // the banner (or the caller) react. The next request falls back to the
    // admin's own token automatically because the interceptor uses
    // `impersonationToken ?? accessToken`.
    if (err.response?.status === 401 && impersonationToken) {
      impersonationToken = null;
      try { sessionStorage.removeItem('cb_impersonation'); } catch { /* noop */ }
      window.dispatchEvent(new CustomEvent('cb:impersonation:expired'));
      return Promise.reject(err);
    }
    if (
      err.response?.status === 401 &&
      authSdk !== null &&
      original &&
      !original.__retriedAfterRefresh
    ) {
      original.__retriedAfterRefresh = true;
      try {
        await authSdk.refreshToken();
      } catch {
        return Promise.reject(err);
      }
      const fresh = authSdk.getAccessToken();
      if (fresh) {
        // Keep module-state token in sync so subsequent calls also pick up new token.
        accessToken = fresh;
        original.headers.Authorization = `Bearer ${fresh}`;
        return axiosInstance.request(original);
      }
    }
    return Promise.reject(err);
  },
);
